import { Localized } from "@fluent/react";
import { History } from "@mui/icons-material";
import { Alert, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { AppUser, updateAppUser } from "../administration";
import { CancelButton } from "../../../app/components/CancelButton";
import { SaveButton } from "../../../app/components/SaveButton";

export interface CompanyUserChange {
    field: string;
    oldValue: string;
    newValue: string;
}

export interface SaveCompanyUserChangesDialogProps {
    changes: CompanyUserChange[];
    user: AppUser;
    onSuccessfullySaved: (updatedUser: AppUser) => void;
    onCancel: () => void;
}

export const SaveCompanyUserChangesDialog = ({
    changes,
    user,
    onSuccessfullySaved,
    onCancel,
}: SaveCompanyUserChangesDialogProps) => {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const dispatch = useAppDispatch();

    const handleCancel = () => {
        setReason("");
        onCancel();
    };

    const handleSave = async () => {
        const trimmedReason = reason.trim();
        if (!trimmedReason || changes.length === 0) return;

        setSaving(true);
        try {
            const updatedUser = await dispatch(updateAppUser({
                ...user,
                changeReason: trimmedReason,
            })).unwrap();
            setReason("");
            dispatch(showSuccess("saved"));
            onSuccessfullySaved(updatedUser);
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <DialogTitle>
                <Localized id="user-save-changes-title">Zapisz zmiany danych</Localized>
            </DialogTitle>
            <DialogContent dividers>
                <TableContainer
                    sx={{
                        mb: 3,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    <Table
                        size="small"
                        sx={{
                            tableLayout: "fixed",
                            "& th, & td": {
                                borderRight: 1,
                                borderColor: "divider",
                                px: 2,
                                py: 1.5,
                                verticalAlign: "middle",
                                overflowWrap: "anywhere",
                            },
                            "& th:last-child, & td:last-child": { borderRight: 0 },
                            "& tbody tr:last-child th, & tbody tr:last-child td": { borderBottom: 0 },
                        }}
                    >
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.50" }}>
                                <TableCell sx={{ width: "30%", fontWeight: 700 }}>
                                    <Localized id="field">Pole</Localized>
                                </TableCell>
                                <TableCell sx={{ width: "35%", fontWeight: 700 }}>
                                    <Localized id="user-value-before-change">Przed zmianą</Localized>
                                </TableCell>
                                <TableCell sx={{ width: "35%", fontWeight: 700 }}>
                                    <Localized id="user-value-after-change">Po zmianie</Localized>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {changes.map((change) => (
                                <TableRow key={change.field} hover sx={{ height: 48 }}>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                                        {change.field}
                                    </TableCell>
                                    <TableCell>{change.oldValue || "—"}</TableCell>
                                    <TableCell sx={{ bgcolor: "#e8f5e9" }}>
                                        {change.newValue || "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {changes.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Localized id="user-no-data-changes">Nie wprowadzono żadnych zmian.</Localized>
                    </Alert>
                )}
                <TextField
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    maxRows={6}
                    label={<Localized id="user-change-reason">Przyczyna zmiany</Localized>}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    disabled={saving}
                />
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2.5 }}
                >
                    <History fontSize="small" />
                    <Localized id="user-changes-saved-in-history">
                        Zmiany i przyczyna zostaną zapisane w historii zdarzeń.
                    </Localized>
                </Typography>
            </DialogContent>
            <DialogActions>
                <CancelButton loading={saving} onClick={handleCancel} />
                <SaveButton loading={saving} onClick={handleSave} disabled={!reason.trim() || changes.length === 0} />
            </DialogActions>
        </>
    );
};
